import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DialogTitle } from '@onesource/components';
import {
  EReviewTimeState,
  REVIEW_REMINDER_COOLDOWN_HOURS,
  REVIEW_REMINDER_NOTE_MAX,
  ReviewTimeStateValues,
  type TReviewReminderResult,
  type TReviewTimeState,
} from '@onesource/schemas';
import { useRemindReviewTeachers } from '@onesource/services';
import { enqueueSnackbar } from 'notistack';
import { FC, useEffect, useMemo, useState } from 'react';
import { MdArrowBack, MdArrowForward, MdClose, MdNotificationsActive } from 'react-icons/md';

import { ReviewReminderDialogSkeleton } from './review-reminder-dialog-skeleton';
import { ReviewReminderPickList, reminderKey } from './review-reminder-pick-list';

const REVIEW_TIME_OPTIONS: Record<TReviewTimeState, { label: string; hint: string }> = {
  [EReviewTimeState.on_time]: { label: 'On time', hint: 'Still inside the review time' },
  [EReviewTimeState.due_soon]: { label: 'Due soon', hint: 'Amber rows' },
  [EReviewTimeState.overdue]: { label: 'Overdue', hint: 'Red rows' },
};

const plural = (count: number, noun: string) => `${count} ${count === 1 ? noun : `${noun}s`}`;

interface RemindTeachersDialogProps {
  open: boolean;
  onClose: () => void;
  spaceId: string;
  /** The space's forms, in order. */
  forms: Array<{ id: string; title: string }>;
  /** Start from the page's own filters. */
  initialFormIds: string[];
  initialReviewTime: TReviewTimeState[];
}

/**
 * Admin: remind teachers, one reminder per teacher and form.
 * 1. Pick the forms and the review times — this is exactly what the teacher's link
 *    opens: Teacher Review, All, those forms' one form, those review times.
 * 2. Tick or untick each teacher + form, add a note, send.
 */
export const RemindTeachersDialog: FC<RemindTeachersDialogProps> = ({
  open,
  onClose,
  spaceId,
  forms,
  initialFormIds,
  initialReviewTime,
}) => {
  const remind = useRemindReviewTeachers(spaceId);
  const [step, setStep] = useState<'setup' | 'pick'>('setup');
  const [formIds, setFormIds] = useState<string[]>([]);
  const [reviewTime, setReviewTime] = useState<TReviewTimeState[]>([]);
  const [preview, setPreview] = useState<TReviewReminderResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setStep('setup');
    setFormIds(initialFormIds.length > 0 ? initialFormIds : forms.map(f => f.id));
    setReviewTime(
      initialReviewTime.length > 0 ? initialReviewTime : [EReviewTimeState.due_soon, EReviewTimeState.overdue],
    );
    setNote('');
    setErrorText(null);
    // Only on open; the page's filters are the starting point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The preview follows the review times, so each form shows what it would send.
  useEffect(() => {
    if (!open) {
      return;
    }
    let isCurrent = true;
    setPreview(null);
    remind
      .mutateAsync({ reviewTime, dryRun: true })
      .then(result => isCurrent && setPreview(result))
      .catch(
        (error: { message?: string }) => isCurrent && setErrorText(error?.message ?? 'Could not load the preview'),
      );
    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reviewTime]);

  /** Per form: submissions waiting and teachers to remind, under the picked review times. */
  const byForm = useMemo(() => {
    const totals = new Map<string, { waiting: number; overdue: number; teachers: number }>();
    for (const r of preview?.recipients ?? []) {
      const id = r.form?.formId ?? '';
      const t = totals.get(id) ?? { waiting: 0, overdue: 0, teachers: 0 };
      t.waiting += r.itemCount;
      t.overdue += r.overdueCount;
      t.teachers += 1;
      totals.set(id, t);
    }
    return totals;
  }, [preview]);

  const lines = useMemo(
    () => (preview?.recipients ?? []).filter(r => r.form && formIds.includes(r.form.formId)),
    [preview, formIds],
  );
  const picked = lines.filter(r => !r.skipped && selected.has(reminderKey(r)));
  const skipped = lines.filter(r => r.skipped).length;
  const isNoteTooLong = note.length > REVIEW_REMINDER_NOTE_MAX;

  const toggleForm = (id: string) =>
    setFormIds(prev => (prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]));
  const toggleTime = (state: TReviewTimeState) =>
    setReviewTime(prev => (prev.includes(state) ? prev.filter(s => s !== state) : [...prev, state]));

  const goToPick = () => {
    setSelected(new Set(lines.filter(r => !r.skipped).map(reminderKey)));
    setStep('pick');
  };

  const handleSend = async () => {
    setIsSending(true);
    try {
      const result = await remind.mutateAsync({
        formIds,
        reviewTime,
        targets: picked.flatMap(r => (r.form ? [{ teacherUserUuid: r.person.userUuid, formId: r.form.formId }] : [])),
        note: note.trim() || undefined,
        dryRun: false,
      });
      enqueueSnackbar(`Sent ${plural(result.sent, 'reminder')}`, { variant: 'success' });
      onClose();
    } catch (error) {
      setErrorText((error as { message?: string })?.message ?? 'Sending failed. Try again.');
    } finally {
      setIsSending(false);
    }
  };

  const timeSummary =
    reviewTime.length === 0 || reviewTime.length === ReviewTimeStateValues.length
      ? 'every waiting and draft submission'
      : reviewTime.map(s => REVIEW_TIME_OPTIONS[s].label.toLowerCase()).join(' or ');

  return (
    <Dialog open={open} onClose={isSending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle onClose={onClose} icon={<MdNotificationsActive size={20} />}>
        Remind teachers
      </DialogTitle>
      <DialogContent>
        {errorText && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorText}
          </Alert>
        )}

        {step === 'setup' ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Each teacher gets one reminder per form. Its link opens their Teacher Review page on that form with the
              review times you pick here — the same list you are looking at.
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              1. Forms
            </Typography>
            {!preview ? (
              <ReviewReminderDialogSkeleton />
            ) : (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 0.5, mb: 2 }}>
                {forms.map(form => {
                  const t = byForm.get(form.id);
                  return (
                    <FormControlLabel
                      key={form.id}
                      sx={{
                        display: 'flex',
                        m: 0,
                        px: 0.5,
                        '& + &': { borderTop: '1px solid', borderColor: 'divider' },
                        '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
                      }}
                      control={
                        <Checkbox
                          size="small"
                          checked={formIds.includes(form.id)}
                          onChange={() => toggleForm(form.id)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }} noWrap>
                            {form.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontVariantNumeric: 'tabular-nums', pr: 1 }}
                          >
                            {t
                              ? `${t.waiting} waiting${t.overdue ? ` · ${t.overdue} overdue` : ''} · ${plural(t.teachers, 'teacher')}`
                              : 'nothing to remind'}
                          </Typography>
                        </Box>
                      }
                    />
                  );
                })}
              </Box>
            )}

            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              2. Review time
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, mb: 1 }}>
              {ReviewTimeStateValues.map(state => (
                <FormControlLabel
                  key={state}
                  control={
                    <Checkbox size="small" checked={reviewTime.includes(state)} onChange={() => toggleTime(state)} />
                  }
                  label={
                    <Typography variant="body2">
                      {REVIEW_TIME_OPTIONS[state].label}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        ({REVIEW_TIME_OPTIONS[state].hint})
                      </Typography>
                    </Typography>
                  }
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Teachers are reminded of {timeSummary}. Their link opens the All view with that form and these review
              times.
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
              3. Tick the teachers to remind
            </Typography>
            {skipped > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {skipped} already reminded about that form in the last {REVIEW_REMINDER_COOLDOWN_HOURS} hours — not sent
                again.
              </Typography>
            )}
            {!!preview?.withoutRecipient && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                {preview.withoutRecipient} matching{' '}
                {preview.withoutRecipient === 1 ? 'submission has' : 'submissions have'} no teacher, so nobody is
                reminded about them. Assign a teacher first.
              </Alert>
            )}
            <ReviewReminderPickList
              items={lines}
              selected={selected}
              onChange={setSelected}
              showAvatar
              label={r => ({
                primary: r.person.name,
                secondary: `${r.form?.formTitle ?? ''} · ${r.itemCount} waiting${r.overdueCount ? ` · ${r.overdueCount} overdue` : ''}`,
              })}
            />
            <TextField
              label="Note (optional)"
              placeholder="Added to every reminder"
              value={note}
              onChange={e => setNote(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              error={isNoteTooLong}
              helperText={`${note.length}/${REVIEW_REMINDER_NOTE_MAX}`}
              sx={{ mt: 2 }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="soft" startIcon={<MdClose />} onClick={onClose} disabled={isSending}>
          Cancel
        </Button>
        {step === 'pick' && (
          <Button variant="outlined" startIcon={<MdArrowBack />} onClick={() => setStep('setup')} disabled={isSending}>
            Back
          </Button>
        )}
        {step === 'setup' ? (
          <Button
            variant="contained"
            startIcon={<MdArrowForward />}
            onClick={goToPick}
            disabled={!preview || formIds.length === 0 || lines.length === 0}
          >
            {lines.length > 0 ? `Next: ${plural(lines.length, 'reminder')}` : 'Nothing to remind'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={picked.length === 0 || isSending || isNoteTooLong}
            startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <MdNotificationsActive />}
          >
            Send {plural(picked.length, 'reminder')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
